import {Badge, Divider, Popover, Table} from "antd";
import {InfoCircleTwoTone} from "@ant-design/icons";
import React from "react";
import {getCompressionColor} from "./ColorUtil";
import Column from "antd/es/table/Column";
import {getAminoAcidAngleVector} from "./GeometryUtil";

export function breakpointsHandler(events,breakpoints){
    if (breakpoints.xxl) return events["xxl"];
    if (breakpoints.xl) return events["xl"];
    if (breakpoints.lg) return events["lg"];
    if (breakpoints.md) return events["md"];
    if (breakpoints.sm) return events["sm"];
    if (breakpoints.xs) return events["xs"];
    return null;

}
export const trim = (value)=> {
    if (value || value === 0) {
        const [integer, decimal] = value.toString().split(".")
        return `${integer}.${!decimal ? "00" : (decimal.length < 3) ? decimal : decimal.substring(0, 2)}`
    }
    return null
}

function dualDataTrim({compression, ascendCompression, ascendAngles}, aminoAcid, dihedralAngles, geometryList) {
    const angle = getAminoAcidAngleVector (aminoAcid, dihedralAngles, geometryList)
    return {
        angle: angle ? trim(angle): angle,
        compression: compression ? trim(compression): compression,
        ascendCompression: ascendCompression ? trim(ascendCompression): ascendCompression,
        ascendAngles: ascendCompression ? [trim(ascendAngles)]: []
    };
}

function extractCompression({compression, ascendCompression, ascendAngles}, aminoAcid, dihedralAngles, geometryList) {

    return {
        compression: compression ? trim(compression):'',
        angles : compression ? trimAnglesInVector(getAminoAcidAngleVector (aminoAcid, dihedralAngles, geometryList)): [],
        ascendCompression: ascendCompression ? trim(ascendCompression):'',
        ascendAngles: ascendAngles ? trimAnglesInVector(ascendAngles):[]
    }
}

function trimAnglesInVector(angles){
    return angles.map(angle => trim(angle))
}

export async function getRemoteData(inputData, isFile) {
    try {
        const response = await fetch("https://lcb.infotech.monash.edu/phisical/checkup/index.php", {
            method: "POST",
            ...(!isFile)&& { headers: {
                    "Content-Type": "application/json",
                }},
            body: inputData
        });

        const response_data = await response.json()
        if (response['status'] === 200) {
            return {
                isSuccess: true,
                data: getData(response_data),
                pymolUrl:getPyMOLUrl(response_data['jobId']),
                jobId:response_data['jobId']
            }
        }
        return {
            isSuccess: false,
            data: response_data["error_message"]
        }

    } catch (e) {
        return {
            isSuccess: false,
            data: "Internal Server Error"
        }
    }

}

export async function sendQueries(inputData) {
    try {
        const response = await fetch("https://lcb.infotech.monash.edu/phisical/checkup/email_handler.php", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: inputData
        });

        if (response['status'] === 200) {
            return {isSuccess: true}
        }
        return {
            isSuccess: false,
            data: "Failed to send your request. Please try again later."
        }

    } catch (e) {
        return {
            isSuccess: false,
            data: "Internal Server Error"
        }
    }
}

export function mapColumns(configurations){

    const columnConfiguration = {
        joint: false,
        marginal:[],
        conditioned:[]
    }

    if (configurations['JOINT_CONFORMATION']) {
        columnConfiguration['joint'] = true
    }
    if (configurations['MARGINAL_CONFORMATIONS']) {

        const marginalTempConformation = configurations['MARGINAL_CONFORMATIONS'].filter(conformation=>  !conformation.includes("All_Individuals") &&
            !conformation.includes("Rama") && !conformation.includes("Rota"))

        const prioritizedConformation = configurations['MARGINAL_CONFORMATIONS'].map(conformation => {
            if (conformation.length === 1 && (conformation.includes("All_Individuals") || conformation.includes("Rama") || conformation.includes("Rota"))) {
                return conformation[0]
            }
            return null;
        })

        let marginalConformation = []
        if (configurations['MARGINAL_CONFORMATIONS'].length !== marginalTempConformation.length) {
            if (prioritizedConformation.includes("Rama")) {
                marginalConformation.push(["Rama"])
            }
            if (prioritizedConformation.includes("Rota")) {
                marginalConformation.push(["Rota"])
            }
            if (prioritizedConformation.includes("All_Individuals")) {
                marginalConformation.push(...allIndividuals)
            }
            marginalConformation = [...marginalConformation, ...marginalTempConformation]
        } else {
            marginalConformation = marginalTempConformation
        }

        columnConfiguration['marginal'] = marginalConformation.map(conformation => (
            {
                name:conformation.map(value=> reverseMapConformationColumnName[value]).join(","),
                key:conformation.map(value=> mapConformationsToBackend[value]).sort().join(",")
            }
        ))
    }

    if (configurations['CONDITIONED_CONFORMATIONS']) {
        columnConfiguration['conditioned'] = configurations['CONDITIONED_CONFORMATIONS'].map(conformation => (
            {
                name:{
                    up: conformation.up.map(value=> reverseMapConformationColumnName[value]).join(","),
                    down: conformation.down.map(value=> reverseMapConformationColumnName[value]).join(","),
                },
                key:{
                    up:conformation.up.map(value=> mapConformationsToBackend[value]).sort().join(","),
                    down: conformation.down.map(value=> mapConformationsToBackend[value]).sort().join(","),
                }
            }
        ))
    }
    return columnConfiguration
}

export function populateConfigs(configurations){
    const requestedConfigs = []

    if (configurations['JOINT_CONFORMATION']) {
        requestedConfigs.push({'type':'JOINT'})
    }

    if (configurations['MARGINAL_CONFORMATIONS']) {
        requestedConfigs.push(...configurations['MARGINAL_CONFORMATIONS'].reduce(function(map, value) {
            const reqObj = {
                type: 'MARGINAL',
                geometryOf:value.map(geometry => (mapConformationsToBackend[geometry])).sort()
            }
            map.set(reqObj.geometryOf.join("|"),reqObj)
            return map;
        }, new Map()).values())
    }
    if (configurations['CONDITIONED_CONFORMATIONS']) {
        requestedConfigs.push(...configurations['CONDITIONED_CONFORMATIONS'].reduce(function(map, value) {
            const reqObj =  {
                type: 'CONDITIONAL',
                geometryOf:value.up.map(geometry => (mapConformationsToBackend[geometry])).sort(),
                geometryGiven:value.down.map(geometry => (mapConformationsToBackend[geometry])).sort()
            }
            map.set(`${reqObj.geometryOf.join("|")}_${reqObj.geometryGiven.join("|")}`,reqObj)
            return map;
        }, new Map()).values())
    }
    return requestedConfigs
}

function getPyMOLUrl(jobId){
    return `https://lcb.infotech.monash.edu/phisical/checkup/RJobs/${jobId}/pymol_scene.tar.gz`
}

function getData(raw_data) {
    const amino_acid_list = []

    raw_data["chains"].forEach(chainEntry => {
        let residueIndex = 1
        chainEntry["residues"].forEach(amino_acid => {
            const aminoAcid = amino_acid["name"]
            const dihedralAngles = amino_acid["angles"] ? amino_acid["angles"] : {}
            const model = amino_acid["model"] ? amino_acid["model"] : "N\\A"
            const amino_acid_entry = {
                key: `${chainEntry["chain"]}:${amino_acid["id"]}:${residueIndex}`,
                chain: chainEntry["chain"],
                index: amino_acid["id"],
                amino_acid: amino_acid["isCisConformation"] !== "true" ? aminoAcid : `cis-${aminoAcid}`,
                ...(amino_acid['geometries'] ? Object.assign({}, ...amino_acid['geometries'].map(value => {
                    const obj = {};
                    if (value['type'] === 'MARGINAL_INDIVIDUAL') {
                        obj[value['names'].join(',')] = dualDataTrim(value, aminoAcid, dihedralAngles, value['names']);
                    } else if (value['type'] === 'CONDITIONAL') {
                        const key = `${value['names'].join(",")}|${value['namesGiven'].join(",")}`;
                        obj[key] = extractCompression(value, aminoAcid, dihedralAngles, value['names']);
                    } else {
                        obj[value['names'].join(',')] = extractCompression(value, aminoAcid, dihedralAngles, value['names']);
                    }
                    return obj;
                })) : {}),
                angles: dihedralAngles,
                isCisConformation: amino_acid["isCisConformation"] === "true",
                isPreProline: amino_acid["isPreProline"] === "true",
                model: model
            }
            amino_acid_list.push(amino_acid_entry)
            residueIndex = residueIndex + 1;
        })
    })

    return amino_acid_list
}

function angleThresholdFormatter(entry){
    return entry ? `${entry.compression || "-"} (${entry.angle || "-"})` : "- (-)"
}

function textFit(text, columnWidth){
    return `${text}${' '.repeat(columnWidth - text?.length||0)}`
}

export function textGenerator(data, dynamicColumns) {
    const columnData = [
        {key: "chain", name: "chain", formatter: (text) => text, marginRepeat: 3},
        {key: "amino_acid", name: "amino acid", formatter: (text) => text, marginRepeat: 3},
        {key: "index", name: "index", formatter: (text) => text, marginRepeat: 3},
        {key: "model", name: "model", formatter: (text) => text, marginRepeat: 25},
        ...dynamicColumns.joint ? [{
            key: "joint",
            name: "joint",
            formatter: (text) => text?.compression || "-",
            marginRepeat: 16
        }]:[],
        ...dynamicColumns.marginal.map(value => {
            return {
                key: value['key'], name: value['key'],
                formatter: isIndividual(value['key']) ? (data) => angleThresholdFormatter(data) : (text) => text?.compression || "-",
                marginRepeat: 16
            }
        }),
        ...dynamicColumns.conditioned.map(value => {
            const key = `${value['key']['up']}|${value['key']['down']}`
            return {key: key, name: key, formatter: (text) => text?.compression || "-", marginRepeat: 16}
        })
    ]

    const columnWidths = []
    const textMatrix = []
    textMatrix.push(["## Each column shows bits of compression and dihedral angle in degrees within parentheses\n"])
    columnData.forEach(columnMeta => {
        const header = `${columnMeta.name}${' '.repeat(columnMeta.marginRepeat)}`
        textMatrix.push([header])
        columnWidths.push(header.length)
    })
    data.forEach(val => {
        columnData.forEach((columnMeta, index) => {
            textMatrix[index].push(textFit(columnMeta.formatter(val[columnMeta.key]), columnWidths[index]))
        })
    })
    return textMatrix[1].map((_, colIndex) => textMatrix.map(row => row[colIndex]).join("")).join("\n")
}

export function isIndividual(key) {
    const individuals =
        {
            'omega': true,
            'phi': true,
            'psi': true,
            'chi1': true,
            'chi2': true,
            'chi3': true,
            'chi4': true,
            'chi5': true,
        }
    return key in individuals;
}

export function defineMerge(dataSet){

    if(dataSet.length < 2){
        return dataSet.map(data =>({...data,mergeColumns:1}))
    }
    dataSet[dataSet.length-1]["mergeColumns"]  = 1
    for (let i = dataSet.length -2 ; i >= 0 ; i--) {
        if(`${dataSet[i]["chain"]}__${dataSet[i]["amino_acid"]}__${dataSet[i]["index"]}` ===`${dataSet[i+1]["chain"]}__${dataSet[i+1]["amino_acid"]}__${dataSet[i+1]["index"]}`){
            const currentVal = dataSet[i+1]["mergeColumns"]
            const newVal = currentVal + 1
            dataSet[i+1]["mergeColumns"] = 0
            dataSet[i]["mergeColumns"] = newVal
        }else {
            dataSet[i+1]["mergeColumns"] = dataSet[i+1]["mergeColumns"]
            dataSet[i]["mergeColumns"] = 1
        }

    }
    return dataSet

}

const formatCell = function ({compression, angles, color}) {
    return (<div
        style={{background: color}}>
        <span >{compression}</span><span style={{color:"#808080", fontSize:"0.8em"}}>{compression ? ' bits' : ''}</span>
        <Divider style={{padding: 0, margin: 5}}/>
        <span style={{
            fontSize: "0.85em",
            color: "#595959"
        }}> {`(${angles})`}</span>
    </div>)
}
export const OUTLIER_LOWER_BOUND = -4;
export const ALLOWED_LOWER_BOUND = 0;
const WithInfo = (baseNode, compression, ascendCompression, angles,ascendAngles, compressionColor, ascendCompressionColor) => {

    const formattedAngles = angles.map( angle => `${angle}º`).join(",")
    const formattedPerturbed = ascendAngles.map( angle => `${angle}º`).join(",")
    const dataSource = [
        {
            key: '1',
            current: {compression: compression, angles:formattedAngles, color:compressionColor},
            perturbed: {compression: ascendCompression, angles:formattedPerturbed, color:ascendCompressionColor}
        }
    ];

    const content = (
        <div style={{display: "flex", gap: "0.3rem", flexDirection: "column"}}>
            <Table dataSource={dataSource} pagination={false} size="small" bordered>
            <Column align={"center"} width={100} title="Current Membership" dataIndex="current" render={formatCell}/>
            <Column align={"center"} width={100} title="Perturbed Membership" dataIndex="perturbed" render={formatCell}/>
            </Table>
        </div>
    )

    return (
        <div style={{paddingLeft: '0.2em', paddingRight: '0.2em'}}>
            <Popover content={content} title="Overlapping memberships">
                <Badge styles={{root: {display: 'inline'}}} count={<InfoCircleTwoTone twoToneColor={'#426ffa'}/>}>
                    {baseNode}
                </Badge>
            </Popover>
        </div>
    )
}
export const getCompressionAdditionalInfo = (baseNode, entry, outlierLowerBound, allowedLowerBound) => {
    const compression = entry ? parseFloat(entry['compression']): null
    if (compression) {
        const ascendCompression = entry['ascendCompression'] ? parseFloat(entry['ascendCompression']): null
        if (ascendCompression) {
            const compressionColor = getCompressionColor(compression, outlierLowerBound, allowedLowerBound)
            const ascendCompressionColor = getCompressionColor(ascendCompression, outlierLowerBound, allowedLowerBound)
            if (compressionColor !== ascendCompressionColor) {
                return WithInfo(baseNode, entry['compression'], trim(entry['ascendCompression']), entry['angle'] ? [entry['angle']]:entry['angles'], entry['ascendAngles'], compressionColor, ascendCompressionColor)
            }
        }
    }
    return baseNode
}

const PRO_CONDITIONED = "pro-conditioned"
export const getModel = (model) => {
    let toolTipText = "Amino acid specific mixture model"
    let modelText = model
    if (model === "a.a specific-pre-proline") {
        toolTipText = "Amino acid specific Pre-Proline mixture model"
        modelText = 'pre-proline'
    } else if (model === PRO_CONDITIONED) {
        toolTipText = "Proline mixture model conditioned on ω"
        modelText = "conditioned \n on ω"
    } else if (model === "cis-proline") {
        toolTipText = "cis-Proline mixture model"
        modelText = "cis-proline"
    } else if (model === "pro-pre-proline-conditioned") {
        toolTipText = "Proline pre-proline mixture model conditioned on ω"
        modelText = "conditioned"
    }

    return {
        toolTipText: toolTipText,
        modelText: modelText
    }
}
export const DEFAULT_CONFIGURATIONS = {
    "JOINT_CONFORMATION": true,
    "MARGINAL_CONFORMATIONS":[["All_Individuals"], ["Rama"], ["Rota"]],
    "CONDITIONED_CONFORMATIONS":[{up: ["Rota"], down: ["Rama"]}]
}

export const mapConformationsToBackend = {
    "All_Individuals": 'all_individuals',
    "Rama": 'rama',
    "Backbone": 'backbone',
    "Rota": 'rota',
    "ω": 'omega',
    "φ": 'phi',
    "ψ": 'psi',
    "χ₁": 'chi1',
    "χ₂": 'chi2',
    "χ₃": 'chi3',
    "χ₄": 'chi4',
    "χ₅": 'chi5',
}

export const conformationSymbols = {

    'omega' : "ω",
    'phi': "φ",
    'psi':"ψ",
    'chi1':"χ₁",
    'chi2':"χ₂",
    'chi3' : "χ₃",
    'chi4': "χ₄",
    'chi5':"χ₅",
}

export const reverseMapConformationColumnName = {
    ...mapConformationsToBackend,
    "ω": 'ω',
    "φ": 'φ',
    "ψ": 'ψ',
    "χ₁": 'χ₁',
    "χ₂": 'χ₂',
    "χ₃": 'χ₃',
    "χ₄": "χ₄",
    "χ₅": "χ₅"
}

const allIndividuals  = [["ω"],["φ"],["ψ"],["χ₁"],["χ₂"],["χ₃"],["χ₄"],["χ₅"]]

export const DEFAULT_MODEL = "Top2018"

export const AMINO_ACID_LIST = ["ALA", "GLY", "CYS", "THR", "SER",
    "VAL", "TRP", "HIS", "TYR", "PHE", "ASN", "PRO", "ASP", "ILE", "LEU",
    "MET", "GLN", "GLU", "LYS", "ARG"]